import { z } from 'zod';

const requiredConsentVersion = 'v1';

export const buildConsentMetadata = (data: {
  required_consent: boolean;
  partner_contacts_consent: boolean;
}) => {
  const timestamp = new Date().toISOString();

  return {
    required_consent: data.required_consent,
    required_consent_at: timestamp,
    required_consent_version: requiredConsentVersion,
    partner_contacts_consent: data.partner_contacts_consent,
    partner_contacts_consent_at: data.partner_contacts_consent
      ? timestamp
      : null,
    partner_contacts_consent_revoked_at: null,
    partner_contacts_consent_version: requiredConsentVersion,
  };
};

export type ConsentMetadata = ReturnType<typeof buildConsentMetadata>;

/** Builds consent metadata for the login flow (required consent only). */
export const buildLoginConsentMetadata = (data: {
  required_consent: boolean;
}) => {
  const timestamp = new Date().toISOString();

  return {
    required_consent: data.required_consent,
    required_consent_at: timestamp,
    required_consent_version: requiredConsentVersion,
  };
};

export type LoginConsentMetadata = ReturnType<typeof buildLoginConsentMetadata>;

export const appendConsentMetadataToUrl = (
  url: URL,
  metadata: ConsentMetadata | LoginConsentMetadata,
) => {
  for (const [key, value] of Object.entries(metadata)) {
    if (value != null) url.searchParams.set(key, String(value));
  }
};

const booleanFromString = z
  .enum(['true', 'false'])
  .transform((v) => v === 'true');

/** Parses consent metadata from URL search params (used in OAuth callback). */
export const consentMetadataUrlSchema = z.object({
  required_consent: booleanFromString,
  required_consent_at: z.string(),
  required_consent_version: z.string(),
  partner_contacts_consent: booleanFromString.optional(),
  partner_contacts_consent_at: z.string().nullable().default(null).optional(),
  partner_contacts_consent_revoked_at: z
    .string()
    .nullable()
    .default(null)
    .optional(),
  partner_contacts_consent_version: z.string().optional(),
});

const userTimestampSchema = z.object({
  created_at: z.string(),
  last_sign_in_at: z.string(),
});

const newUserTimestampThresholdMs = 10_000;

export const wasUserJustCreated = (
  user:
    | { created_at?: string; last_sign_in_at?: string | null }
    | null
    | undefined,
): boolean => {
  const parsed = userTimestampSchema.safeParse(user);
  if (!parsed.success) return false;

  const createdAt = Date.parse(parsed.data.created_at);
  const lastSignInAt = Date.parse(parsed.data.last_sign_in_at);

  if (Number.isNaN(createdAt) || Number.isNaN(lastSignInAt)) return false;

  return Math.abs(lastSignInAt - createdAt) <= newUserTimestampThresholdMs;
};
